import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Brain, Upload, FileText, Loader2, Trash2, Eye, AlertTriangle,
  CheckCircle, XCircle, Clock, RefreshCw, Settings as SettingsIcon,
  TrendingUp, FileBarChart, Sparkles, Building2, User as UserIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ExpedienteSelector } from '../components/ExpedienteSelector';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;

const ESTADO_COLOR = {
  subido: 'bg-gray-100 text-gray-700',
  convirtiendo: 'bg-blue-100 text-blue-700',
  convertido: 'bg-blue-100 text-blue-800',
  sin_ocr: 'bg-orange-100 text-orange-800',
  analizando: 'bg-purple-100 text-purple-700',
  analizado: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  descartado: 'bg-gray-100 text-gray-500',
  expediente_creado: 'bg-emerald-100 text-emerald-800',
  expediente_anadido: 'bg-emerald-100 text-emerald-800',
  extrajudicial_creado: 'bg-emerald-100 text-emerald-800',
  pausado: 'bg-yellow-100 text-yellow-800',
};

const ESTADO_LABEL = {
  subido: 'Subido',
  convirtiendo: 'Convirtiendo',
  convertido: 'Listo para analizar',
  sin_ocr: 'OCR no resuelto',
  analizando: 'Analizando con IA…',
  analizado: 'Analizado',
  error: 'Error',
  descartado: 'Descartado',
  expediente_creado: 'Expediente creado',
  expediente_anadido: 'Añadido a expediente',
  extrajudicial_creado: 'Reclamación creada',
  pausado: 'En pausa',
};

const formatEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

export default function AnalisisHipotecas() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [providers, setProviders] = useState(null);
  const [providerSel, setProviderSel] = useState('anthropic');
  const [modelSel, setModelSel] = useState('claude-sonnet-4-5-20250929');
  // IDs que tras CONVERTIDO deben lanzarse a análisis automáticamente
  const [pendingAuto, setPendingAuto] = useState({}); // {id: {provider, model}}
  const [uploadProgress, setUploadProgress] = useState(0); // 0-100
  const [expediente, setExpediente] = useState(null);

  const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

  const loadList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis?tipo=hipoteca`, tokenHeader());
      setItems(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar la lista', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProviders = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/providers`, tokenHeader());
      setProviders(r.data);
      setProviderSel(r.data.default_provider);
      setModelSel(r.data.default_model);
    } catch (e) {
      console.warn('No providers', e);
    }
  }, []);

  useEffect(() => {
    loadList();
    loadProviders();
  }, [loadList, loadProviders]);

  // Polling de items en estado 'analizando' o 'convirtiendo' + auto-analizar al convertir
  useEffect(() => {
    // Auto-disparar análisis cuando un item pendiente pase a 'convertido'
    const pendingIds = Object.keys(pendingAuto).map((s) => parseInt(s, 10));
    if (pendingIds.length > 0) {
      const item = items.find((i) => pendingIds.includes(i.id) && i.estado === 'convertido');
      if (item) {
        const cfg = pendingAuto[item.id];
        // Limpiar del pending antes de la llamada para evitar dobles disparos
        setPendingAuto((prev) => {
          const c = { ...prev };
          delete c[item.id];
          return c;
        });
        axios.post(
          `${API}/api/hipotecas/analisis/${item.id}/analizar`,
          { provider: cfg.provider, model: cfg.model },
          tokenHeader(),
        ).then(() => {
          toast({ title: 'Análisis iniciado', description: `${item.nombre_archivo_original} — la IA está procesando.` });
          loadList();
        }).catch((err) => {
          toast({ title: 'Error al iniciar análisis', description: err.response?.data?.detail || err.message, variant: 'destructive' });
        });
      }
      // Si un pending pasó a 'sin_ocr' o 'error', limpiarlo (no relanzar)
      const fallidos = items.filter((i) => pendingIds.includes(i.id) && ['sin_ocr', 'error'].includes(i.estado));
      if (fallidos.length > 0) {
        setPendingAuto((prev) => {
          const c = { ...prev };
          fallidos.forEach((f) => { delete c[f.id]; });
          return c;
        });
      }
    }

    const enProceso = items.some((i) => ['analizando', 'convirtiendo'].includes(i.estado));
    if (!enProceso) return;
    const t = setTimeout(loadList, 3000);
    return () => clearTimeout(t);
  }, [items, loadList, pendingAuto]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'Archivo demasiado grande', description: 'Máx 50 MB', variant: 'destructive' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo_analisis', 'hipoteca');
    if (expediente) fd.append('expediente_id', expediente.id);
    setUploading(true);
    setUploadProgress(0);
    try {
      const r = await axios.post(`${API}/api/hipotecas/analisis`, fd, {
        ...tokenHeader(),
        headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            const pct = Math.round((evt.loaded / evt.total) * 100);
            setUploadProgress(pct);
          } else {
            setUploadProgress((p) => Math.min(95, p + 5));
          }
        },
      });
      const nuevo = r.data;
      setUploadProgress(100);
      // Marcamos para auto-analizar en cuanto la conversión termine
      setPendingAuto((prev) => ({ ...prev, [nuevo.id]: { provider: providerSel, model: modelSel } }));
      toast({
        title: 'Archivo subido',
        description: 'Convirtiendo a Markdown… El análisis IA se lanzará automáticamente al terminar.',
      });
      await loadList();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const lanzarAnalisis = async (id) => {
    console.log('[Hipotecas] lanzarAnalisis click', { id, provider: providerSel, model: modelSel });
    toast({ title: 'Iniciando análisis…', description: `Modelo: ${providerSel}/${modelSel}` });
    try {
      await axios.post(
        `${API}/api/hipotecas/analisis/${id}/analizar`,
        { provider: providerSel, model: modelSel },
        tokenHeader()
      );
      toast({ title: 'Análisis iniciado', description: 'Redirigiendo al detalle…' });
      // Navegamos al detalle para que el usuario vea la barra de progreso
      navigate(`/expedientes/analisis-hipotecas/${id}`);
    } catch (err) {
      console.error('[Hipotecas] lanzarAnalisis error', err);
      const detail = err.response?.data?.detail || err.message || 'Error desconocido';
      const status = err.response?.status;
      toast({
        title: `Error al iniciar análisis${status ? ` (${status})` : ''}`,
        description: detail,
        variant: 'destructive',
      });
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar este análisis y todos sus archivos?')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
      toast({ title: 'Análisis eliminado' });
      await loadList();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="analisis-hipotecas-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-purple-600" />
            Análisis IA Hipotecas
          </h1>
          <p className="text-gray-600 mt-1">
            Análisis automatizado de cláusulas abusivas en escrituras hipotecarias
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/expedientes/analisis-hipotecas/configuracion')}
            data-testid="btn-configuracion"
          >
            <SettingsIcon className="w-4 h-4 mr-2" />
            Skills / Configuración
          </Button>
          <Button variant="outline" onClick={loadList} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      {/* Upload */}
      <Card className="border-2 border-dashed border-purple-300 bg-purple-50/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Upload className="w-5 h-5 text-purple-600" />
                Sube una escritura hipotecaria
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Formatos: PDF (con OCR automático si es un escaneo), DOCX, MD. Máx 50 MB. La IA detectará
                cláusulas abusivas y generará un informe estimando el importe recuperable.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {providers && (
                <>
                  <Select value={providerSel} onValueChange={(v) => { setProviderSel(v); setModelSel(providers.modelos[v]?.[0] || ''); }}>
                    <SelectTrigger className="w-[200px]" data-testid="select-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.keys(providers.modelos).map((p) => (
                        <SelectItem key={p} value={p}>
                          {providers.display_names?.[p]
                            || (p === 'anthropic' ? 'Claude' : p === 'openai' ? 'OpenAI' : p === 'gemini' ? 'Gemini' : p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={modelSel} onValueChange={setModelSel}>
                    <SelectTrigger className="w-[260px]" data-testid="select-model">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(providers.modelos[providerSel] || []).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-1">Asociar a expediente (opcional)</p>
                <ExpedienteSelector value={expediente} onChange={setExpediente} testId="hip-expediente" />
              </div>
              <label className="inline-flex">
                {uploading ? (
                  <div
                    className="relative inline-flex items-center justify-center min-w-[200px] h-10 px-4 rounded-md overflow-hidden bg-purple-100 text-white font-medium text-sm select-none cursor-not-allowed"
                    data-testid="btn-upload-progress"
                  >
                    {/* Fondo coloreado animado según progreso real */}
                    <div
                      className="absolute inset-y-0 left-0 bg-gradient-to-r from-purple-600 to-purple-500 transition-all duration-200 ease-out"
                      style={{ width: `${uploadProgress}%` }}
                    />
                    {/* Contenido encima del fill */}
                    <span className="relative flex items-center gap-2 z-10 mix-blend-difference">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      {uploadProgress < 100
                        ? `Subiendo ${uploadProgress}%`
                        : 'Procesando…'}
                    </span>
                  </div>
                ) : (
                  <>
                    <Button asChild data-testid="btn-upload">
                      <span className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Subir hipoteca
                      </span>
                    </Button>
                    <input type="file" hidden accept=".pdf,.docx,.doc,.md" onChange={handleFile} data-testid="input-file" />
                  </>
                )}
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats rápidos */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Total análisis</p>
                <p className="text-2xl font-bold">{items.length}</p>
              </div>
              <FileBarChart className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-analizados">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Analizados</p>
                <p className="text-2xl font-bold text-green-700">{items.filter((i) => i.estado === 'analizado').length}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-recuperable">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Recuperable total</p>
                <p className="text-2xl font-bold text-emerald-700">
                  {formatEur(items.reduce((s, i) => s + (i.importe_total_recuperable || 0), 0))}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
        <Card data-testid="kpi-en-proceso">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">En proceso</p>
                <p className="text-2xl font-bold text-blue-700">
                  {items.filter((i) => ['analizando', 'convirtiendo'].includes(i.estado)).length}
                </p>
              </div>
              <Loader2 className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Lista */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Análisis</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-purple-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Sube tu primera escritura para comenzar el análisis</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="lista-analisis">
              {items.map((a) => (
                <div
                  key={a.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`item-analisis-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-5 h-5 text-purple-500 flex-shrink-0" />
                      <span className="font-medium truncate" title={a.nombre_archivo_original}>
                        {a.nombre_archivo_original}
                      </span>
                      <Badge className={ESTADO_COLOR[a.estado] || 'bg-gray-100'}>
                        {(a.estado === 'convirtiendo' || a.estado === 'analizando') && (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />
                        )}
                        {ESTADO_LABEL[a.estado] || a.estado}
                      </Badge>
                      {pendingAuto[a.id] && a.estado === 'convirtiendo' && (
                        <span className="text-xs text-purple-700">→ análisis IA arrancará al terminar</span>
                      )}
                    </div>
                    {a.paso_actual && ['analizando', 'convirtiendo'].includes(a.estado) && (
                      <p className="text-xs text-blue-700 mt-1 truncate" title={a.paso_actual}>
                        {a.paso_actual}
                      </p>
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4">
                      <span><Clock className="w-3 h-3 inline mr-1" />{new Date(a.created_at).toLocaleString('es-ES')}</span>
                      {a.cliente_nombre && <span><UserIcon className="w-3 h-3 inline mr-1" />{a.cliente_nombre}</span>}
                      {a.expediente_numero && (
                        <span className="text-emerald-700 font-medium">Expediente {a.expediente_numero}</span>
                      )}
                      {a.banco_nombre && <span><Building2 className="w-3 h-3 inline mr-1" />{a.banco_nombre}</span>}
                      {a.num_clausulas_detectadas > 0 && (
                        <span className="text-purple-700 font-medium">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />
                          {a.num_clausulas_detectadas} cláusulas
                        </span>
                      )}
                      {a.importe_total_recuperable > 0 && (
                        <span className="text-emerald-700 font-medium">
                          {formatEur(a.importe_total_recuperable)} recuperable
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {a.estado === 'convertido' && (
                      <Button size="sm" onClick={() => lanzarAnalisis(a.id)} data-testid={`btn-analizar-${a.id}`}>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Analizar
                      </Button>
                    )}
                    {a.estado === 'convirtiendo' && (
                      <Button size="sm" disabled className="opacity-60">
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Convirtiendo…
                      </Button>
                    )}
                    {a.estado === 'sin_ocr' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={async () => {
                          try {
                            await axios.post(`${API}/api/hipotecas/analisis/${a.id}/reintentar-ocr`, {}, tokenHeader());
                            toast({ title: 'OCR relanzado', description: 'Se intentará OCR local y, si no, OCR por IA.' });
                            await loadList();
                          } catch (err) {
                            toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
                          }
                        }}
                        data-testid={`btn-ocr-${a.id}`}
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Reintentar OCR
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/expedientes/analisis-hipotecas/${a.id}`)}
                      data-testid={`btn-ver-${a.id}`}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => borrar(a.id)} data-testid={`btn-borrar-${a.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
